if (!customElements.get('bm-search-panel')) {
  customElements.define('bm-search-panel', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.input = this.querySelector('input[name="q"]');
      this.form = this.querySelector('form');
      this.results = this.querySelector('[data-bm-search-results]');
      this.home = this.querySelector('[data-bm-search-home]');
      this.loading = this.querySelector('[data-bm-search-loading]');
      this.status = this.querySelector('[data-bm-search-status]');
      this.fallback = this.querySelector('[data-bm-search-fallback]');
      this.reset = this.querySelector('[type="reset"]');
      this.recent = this.querySelector('[data-bm-search-recent]');
      this.closeButton = this.querySelector('[data-bm-search-close]');
      this.closeLabel = this.closeButton.getAttribute('aria-label');
      this.cache = new Map();
      this.version = 0;
      this.activeIndex = -1;
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-bm-search-open]');
        if (!trigger || trigger.getAttribute('aria-controls') !== this.dialog.id) return;
        event.preventDefault();
        this.open(trigger);
      }, options);
      this.querySelector('[data-bm-search-close]').addEventListener('click', () => this.dismiss(), options);
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.close();
      }, options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.ignoreEscapeRelease = true;
        this.dismiss();
      }, options);
      document.addEventListener('keyup', event => {
        if (!this.ignoreEscapeRelease || event.key !== 'Escape') return;
        this.ignoreEscapeRelease = false;
        event.stopPropagation();
      }, { ...options, capture: true });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dismiss();
      }, options);
      this.input.addEventListener('input', () => this.search(), options);
      this.input.addEventListener('keydown', event => this.onKeydown(event), options);
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (this.isClosing) return;
        if (!this.input.value.trim()) {
          this.input.focus();
          return;
        }
        this.remember();
        this.search(true, true);
      }, options);
      this.form.addEventListener('reset', event => {
        event.preventDefault();
        this.input.value = '';
        this.search();
        this.input.focus();
      }, options);
      this.addEventListener('click', event => {
        if (this.isClosing) return;
        const suggestion = event.target.closest('[data-bm-query]');
        if (suggestion) {
          this.input.value = suggestion.dataset.bmQuery;
          this.input.focus();
          this.search(true);
        }
        const link = event.target.closest('a[href]');
        if (link && this.dialog.contains(link) && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && (!link.target || link.target === '_self') && !link.hasAttribute('download')) {
          this.remember();
          this.dismiss(false);
        }
        if (event.target.closest('[data-bm-clear-recent]')) {
          try { sessionStorage.removeItem('blackmask:recent-searches:v1'); } catch (_) {}
          this.renderRecent();
          this.input.focus({ preventScroll: true });
        }
      }, options);
      this.updateViewport = () => {
        const viewport = window.visualViewport;
        const viewportTop = viewport?.offsetTop || 0;
        const viewportHeight = viewport?.height || window.innerHeight;
        const nav = document.querySelector('.header.header--boxed-mode');
        const frame = this.drawer?.querySelector('#menu-drawer') || nav;
        const rect = frame?.getBoundingClientRect();
        const top = this.drawer ? viewportTop + 6 : Math.max(viewportTop + 8, rect?.bottom || 24);
        this.dialog.style.setProperty('--bm-search-top', `${top}px`);
        this.dialog.style.setProperty('--bm-search-left', `${rect?.left || 0}px`);
        this.dialog.style.setProperty('--bm-search-width', `${rect?.width || document.documentElement.clientWidth}px`);
        this.dialog.style.setProperty('--bm-search-height', `${Math.max(0, viewportTop + viewportHeight - top - (this.drawer ? 6 : 16))}px`);
      };
      window.visualViewport?.addEventListener('resize', this.updateViewport, options);
      window.visualViewport?.addEventListener('scroll', this.updateViewport, options);
      window.addEventListener('resize', this.updateViewport, options);
      window.addEventListener('pagehide', () => this.dismiss(false), options);
      this.renderRecent();
    }

    disconnectedCallback() {
      this.dismiss(false);
      this.lifecycle?.abort();
      this.request?.abort();
      clearTimeout(this.timer);
    }

    open(trigger) {
      clearTimeout(this.closeTimer);
      this.isClosing = false;
      this.dialog.classList.remove('is-closing');
      const drawer = trigger.closest('header-drawer');
      this.drawer = drawer?.isOpen ? drawer : null;
      this.returnFocus = trigger;
      this.drawer?.suspendForSearch();
      this.dialog.classList.toggle('bm-search-dialog--menu', Boolean(this.drawer));
      this.closeButton.setAttribute('aria-label', this.drawer ? this.closeButton.dataset.menuLabel : this.closeLabel);
      this.header = document.querySelector('sticky-header');
      if (!this.drawer && this.header) {
        const rect = this.header.getBoundingClientRect();
        const section = this.header.closest('.shopify-section-header');
        section.style.setProperty('--bm-search-header-top', `${rect.top}px`);
        section.style.setProperty('--bm-search-header-height', `${section.getBoundingClientRect().height}px`);
      }
      this.isOpen = true;
      document.documentElement.classList.add('bm-search-open');
      document.body.classList.add('bm-search-open');
      document.body.classList.toggle('bm-search-anchored', !this.drawer);
      if (this.header) this.header.preventHide = true;
      this.updateViewport();
      if (!this.dialog.open) this.dialog.showModal();
      this.trigger = trigger;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.renderRecent();
      this.input.focus({ preventScroll: true });
      if (this.input.value.trim()) this.search(true);
    }

    dismiss(restoreFocus = true) {
      if (!this.isOpen) return;
      if (!restoreFocus || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.finishDismiss(restoreFocus);
        return;
      }
      if (this.isClosing) return;
      this.isClosing = true;
      // Cancel pending results before the exit animation can navigate away.
      this.version++;
      this.request?.abort();
      clearTimeout(this.timer);
      this.input.blur();
      this.drawer?.resumeFromSearch(null);
      this.dialog.classList.add('is-closing');
      this.closeTimer = setTimeout(() => this.finishDismiss(true), 220);
    }

    finishDismiss(restoreFocus) {
      clearTimeout(this.closeTimer);
      if (this.dialog?.open) this.dialog.close();
      this.close(restoreFocus);
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.isClosing = false;
      clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-closing');
      this.version++;
      this.request?.abort();
      clearTimeout(this.timer);
      this.setLoading(false);
      this.input.setAttribute('aria-expanded', 'false');
      this.clearActive();
      document.documentElement.classList.remove('bm-search-open');
      document.body.classList.remove('bm-search-open');
      document.body.classList.remove('bm-search-anchored');
      const section = this.header?.closest('.shopify-section-header');
      section?.style.removeProperty('--bm-search-header-top');
      section?.style.removeProperty('--bm-search-header-height');
      if (this.header) this.header.preventHide = false;
      this.trigger?.setAttribute('aria-expanded', 'false');
      const drawer = this.drawer;
      this.drawer = null;
      if (drawer) {
        if (restoreFocus && drawer.isOpen && drawer.openBtn.getClientRects().length) {
          drawer.resumeFromSearch(this.returnFocus);
          if (this.header) this.header.preventHide = true;
          return;
        }
        drawer.closeMenuDrawer(drawer.openBtn, { immediate: true, restoreFocus: false });
      }
      if (!restoreFocus) return;
      const focusTarget = this.returnFocus?.getClientRects().length ? this.returnFocus : [...document.querySelectorAll('[data-bm-search-open]')].find(trigger => trigger.getClientRects().length);
      focusTarget?.focus({ preventScroll: true });
    }

    search(immediate = false, openExactMatch = false) {
      if (this.isClosing) return;
      const query = this.input.value.trim().slice(0, 200);
      const version = ++this.version;
      this.request?.abort();
      clearTimeout(this.timer);
      this.clearActive();
      this.input.setAttribute('aria-expanded', 'false');
      this.reset.hidden = !query;
      this.fallback.hidden = true;
      this.home.hidden = Boolean(query);
      this.results.hidden = true;
      if (!query) {
        this.results.replaceChildren();
        this.setLoading(false);
        this.status.textContent = '';
        this.renderRecent();
        return;
      }
      this.setLoading(true);
      this.timer = setTimeout(() => this.fetchResults(query, version, openExactMatch), immediate ? 0 : 180);
    }

    async fetchResults(query, version, openExactMatch = false) {
      const key = query.toLocaleLowerCase();
      try {
        let markup = this.cache.get(key);
        if (!markup) {
          this.request = new AbortController();
          const url = new URL(this.dataset.predictiveUrl, window.location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('resources[type]', 'product,query,collection,page');
          url.searchParams.set('resources[limit]', '6');
          url.searchParams.set('resources[limit_scope]', 'each');
          url.searchParams.set('section_id', 'bm-search-results');
          const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Search unavailable');
          markup = await response.text();
        }
        if (version !== this.version || !this.dialog.open) return;
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const content = parsed.querySelector('.bm-search-response');
        if (!content) throw new Error('Search unavailable');
        if (!this.cache.has(key)) {
          if (this.cache.size >= 30) this.cache.delete(this.cache.keys().next().value);
          this.cache.set(key, markup);
        }
        this.results.replaceChildren(content);
        this.results.hidden = false;
        const exactProduct = this.findExactProduct(query);
        const exactOption = exactProduct?.closest('.bm-search__option');
        if (exactOption) exactOption.parentElement.prepend(exactOption);
        this.setLoading(false);
        this.input.setAttribute('aria-expanded', String(Number(content.dataset.resultCount) > 0));
        this.status.textContent = this.dataset.count.replace('[count]', content.dataset.resultCount || '0');
        if (openExactMatch) exactProduct?.click();
      } catch (error) {
        if (error.name === 'AbortError' || version !== this.version || !this.dialog.open) return;
        this.setLoading(false);
        this.fallback.hidden = false;
        const url = new URL(this.form.action);
        url.searchParams.set('q', query);
        url.searchParams.set('options[prefix]', 'last');
        this.fallback.querySelector('a').href = url.toString();
        this.status.textContent = this.dataset.error;
      }
    }

    findExactProduct(query) {
      // Match a whole product name or handle, never an ambiguous partial query.
      const normalize = value => (value || '').normalize('NFKC').toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').replace(/\bblack\s?mask\b/g, '').trim().replace(/\s+/g, ' ');
      const term = normalize(query);
      if (!term) return null;
      const products = [...this.results.querySelectorAll('[data-bm-product-title]')];
      const handles = products.filter(link => normalize(link.dataset.bmProductHandle) === term);
      const matches = handles.length ? handles : products.filter(link => normalize(link.dataset.bmProductTitle) === term);
      const destinations = new Map(matches.map(link => [link.href, link]));
      return destinations.size === 1 ? destinations.values().next().value : null;
    }

    setLoading(isLoading) {
      this.loading.hidden = !isLoading;
      this.results.setAttribute('aria-busy', String(isLoading));
      if (isLoading) this.status.textContent = this.dataset.loading;
    }

    clearActive() {
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.results.querySelectorAll('[aria-selected="true"]').forEach(item => item.setAttribute('aria-selected', 'false'));
    }

    onKeydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.ignoreEscapeRelease = true;
        this.dismiss();
        return;
      }
      const options = [...this.results.querySelectorAll('[role="option"]')];
      if (this.results.hidden || !options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = this.activeIndex < 0 ? (direction > 0 ? 0 : options.length - 1) : (this.activeIndex + direction + options.length) % options.length;
        options.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.activeIndex)));
        const active = options[this.activeIndex];
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        this.remember();
        options[this.activeIndex].querySelector('a, button')?.click();
      }
    }

    readRecent() {
      try {
        const data = JSON.parse(sessionStorage.getItem('blackmask:recent-searches:v1') || '[]');
        return Array.isArray(data) ? data.filter(item => typeof item === 'string' && item.length > 0 && item.length <= 80).slice(0, 4) : [];
      } catch (_) { return []; }
    }

    remember() {
      const query = this.input.value.trim().slice(0, 80);
      if (!query) return;
      const items = [query, ...this.readRecent().filter(item => item.toLocaleLowerCase() !== query.toLocaleLowerCase())].slice(0, 4);
      try { sessionStorage.setItem('blackmask:recent-searches:v1', JSON.stringify(items)); } catch (_) {}
    }

    renderRecent() {
      const items = this.readRecent();
      const list = this.querySelector('[data-bm-recent-list]');
      list.replaceChildren(...items.map(query => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.bmQuery = query;
        button.textContent = query;
        return button;
      }));
      this.recent.hidden = !items.length;
    }
  });
}
